import { Link } from 'react-router-dom'
import { getMyOrgPractices } from '../../api/staff'
import { listInvoices } from '../../api/invoices'
import { ErrorBox } from '../ui/Card'
import { useFetch } from '../../hooks/useFetch'
import { formatCurrency } from '@/lib/currency'
import type { InvoiceListItem } from '../../types/invoices'
import { DashboardWidgetCard } from './DashboardWidgetCard'

export function OrgOutstandingInvoicesWidget() {
  const { data: orgPractices } = useFetch(() => getMyOrgPractices(), [])
  const practiceIds = (orgPractices ?? []).map(p => p.id)

  const { data, loading, error } = useFetch(
    () => practiceIds.length === 0
      ? Promise.resolve([[], []] as [InvoiceListItem[], InvoiceListItem[]])
      : Promise.all([
          Promise.all(practiceIds.map(id => listInvoices({ practice_id: id, status: 'overdue', limit: 100 }))).then(l => l.flat()),
          Promise.all(practiceIds.map(id => listInvoices({ practice_id: id, status: 'sent', limit: 100 }))).then(l => l.flat()),
        ]),
    [practiceIds.join(',')],
  )
  const [overdue, sent] = data ?? [[], []]
  const overdueTotal = overdue.reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0)
  const sentTotal = sent.reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0)
  const topOverdue = [...overdue]
    .sort((a, b) => (a.due_date ?? '').localeCompare(b.due_date ?? ''))
    .slice(0, 5)

  return (
    <DashboardWidgetCard title="Outstanding Invoices — All Practices" viewAllHref="/invoices?status=overdue">
      <div className="px-5 py-4">
        <div className="grid grid-cols-2 gap-4 mb-1">
          <div>
            <div className="text-[0.72rem] font-semibold text-text-3 uppercase tracking-[0.05em] mb-[0.3rem]">Overdue</div>
            <div className="text-[1.5rem] font-bold leading-none text-danger-text">
              {loading ? '—' : formatCurrency(overdueTotal)}
            </div>
            <div className="text-[0.78rem] text-text-3 mt-1">
              {loading ? '' : `${overdue.length} invoice${overdue.length === 1 ? '' : 's'}`}
            </div>
          </div>
          <div>
            <div className="text-[0.72rem] font-semibold text-text-3 uppercase tracking-[0.05em] mb-[0.3rem]">Sent, awaiting payment</div>
            <div className="text-[1.5rem] font-bold leading-none text-text-heading">
              {loading ? '—' : formatCurrency(sentTotal)}
            </div>
            <div className="text-[0.78rem] text-text-3 mt-1">
              {loading ? '' : `${sent.length} invoice${sent.length === 1 ? '' : 's'}`}
            </div>
          </div>
        </div>

        {error && <ErrorBox className="mt-4 mb-0">Could not load invoices.</ErrorBox>}

        {!loading && !error && topOverdue.length > 0 && (
          <div className="border-t border-border mt-4 pt-3">
            <div className="text-[0.72rem] font-semibold text-text-3 uppercase tracking-[0.05em] mb-2">Most overdue</div>
            <div className="flex flex-col gap-1">
              {topOverdue.map(inv => (
                <Link
                  key={inv.id}
                  to={`/invoices/${inv.id}`}
                  className="flex items-center justify-between text-[0.85rem] no-underline hover:bg-surface-hover -mx-2 px-2 py-[0.35rem] rounded transition-colors duration-150"
                >
                  <span className="text-text">{inv.client_name ?? inv.invoice_number}</span>
                  <span className="text-danger-text font-medium">{formatCurrency(inv.total_amount)}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {!loading && !error && overdue.length === 0 && sent.length === 0 && (
          <div className="text-[0.85rem] text-text-3 text-center py-3 mt-2">No outstanding invoices.</div>
        )}
      </div>
    </DashboardWidgetCard>
  )
}
